// Full-document render of the Oscar pay-later completion view: the checkout
// header + flash messages in the PageSkeleton header slot, the CyberSource
// fingerprint + Google Maps loader scripts, and the
// `#complete-deferred-payment-app` SPA mount fed the view's init_data payload
// (the checkout island reads it from the data-init-data attribute).
import React from "react";

import { templates } from "@reactivated";

import { ExternalScript } from "@thelabnyc/thelabui/src/components/ExternalScript";

import { DeferredPaymentApp } from "../apps/checkout/DeferredPaymentApp";
import { PageSkeleton } from "../components/PageSkeleton";
import { Template as CheckoutHeader } from "./CheckoutHeaderTemplate";
import { Template as MessageBanner } from "./MessageBannerTemplate";

export const Template = (
    props: templates.CompleteDeferredPaymentDocumentTemplate,
): React.JSX.Element => {
    return (
        <PageSkeleton
            pageChrome={props.page_chrome}
            /* The header slot renders the flash queue as MessageBanners (plus a
               trailing blank one that surfaces the Redux payment_error) and the
               checkout header, so the default messages block is suppressed. */
            messages={false}
            header={
                <>
                    {props.page_chrome.messages.map((message, i) => (
                        <MessageBanner
                            key={i}
                            tags={message.tags}
                            message={message.message}
                        />
                    ))}
                    <MessageBanner tags="" message="" />
                    <CheckoutHeader {...props.checkout_header} />
                </>
            }
        >
            {/* React 19 hoists this <meta> into <head>; the description meta
                renders from page_chrome.search_description. */}
            <meta name="chatTags" content="checkout" />
            {/* CyberSource device fingerprint (fraud-critical). Under the CSP a
                third-party script can no longer block the parser, so a loader
                inserts it; nothing orders it against the app bundle. */}
            <p
                style={{
                    background: `url(${props.fingerprint_img_1_url})`,
                }}
            />
            <img src={props.fingerprint_img_2_url} alt="" />
            <ExternalScript
                src={props.fingerprint_js_url}
                type="text/javascript"
            />
            {/* Google Maps Places powers the address-autocomplete inputs; it
                still loads well before the address step renders. */}
            <ExternalScript
                src={`https://maps.googleapis.com/maps/api/js?v=3&key=${props.google_maps_api_key}&libraries=places`}
                type="text/javascript"
                async={true}
            />
            {/* The complete-deferred-payment app lives in the document's React
                tree (mounted client-side by DeferredPaymentApp; the dependency
                tree is not SSR-safe). Its init_data payload is threaded in as a
                prop (from the reactivated document prop, carried to the client
                via __PRELOADED_PROPS__) rather than a data-init-data attribute
                the old imperative island read off the DOM. */}
            <div className="l-capped-width">
                <div
                    id="complete-deferred-payment-app"
                    className="checkout-app-container"
                >
                    <DeferredPaymentApp initData={props.init_data} />
                </div>
            </div>
        </PageSkeleton>
    );
};
